import React, { useRef, useState, forwardRef, useEffect, Fragment } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Emitter, Vec2 } from '@visiba-cortex/std';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Logger } from '@visiba-cortex/logger';
import DOMPurify from 'dompurify';

/**
 * # cortex:react-ui-std:helper:focusContext
 *
 * Simple storage to mutate the current focused DOM element.
 */
const focusContext = {
  current: null
};

// The base_button component does not include "cursor: pointer;" due to
// official standards specifying that: "The cursor is a pointer that indicates
// a link." https://www.w3.org/TR/CSS21/ui.html#propdef-cursor
//
// Microsoft and Apple also specifies that the "correct" interaction is to
// reserve the pointing hand to URL links
// https://docs.microsoft.com/en-gb/windows/win32/uxguide/inter-mouse?redirectedfrom=MSDN
// https://developer.apple.com/design/human-interface-guidelines/macos/user-interaction/mouse-and-trackpad/
//
// If you'd want to have buttons with cursors, that's fine, but you'll need to
// extend the base_button component in order to get that interaction
const elements$c = {
  content: styled.button.withConfig({
    displayName: "base_buttoncomponent__content",
    componentId: "sc-1imqvhh-0"
  })(["border:none;margin:0;padding:0;width:auto;overflow:visible;background:transparent;color:inherit;font:inherit;line-height:normal;-webkit-font-smoothing:inherit;-moz-osx-font-smoothing:inherit;-webkit-appearance:none;"])
};
/**
 * # cortex:react-ui-std:comp:BaseButton
 */

const BaseButton = ({ ...props
}) => {
  // Takes out these values to be used as variables here.
  const {
    children,
    className,
    disabled,
    // Takes out focus events for override.
    onBlur,
    onFocus,
    // Takes out mouse events for override.
    onClick,
    onMouseDown,
    onMouseUp,
    onMouseEnter,
    onMouseLeave,
    // Takes out keyboard events for override.
    onKeyUp,
    onKeyDown,
    // Takes out touch events for override.
    onTouchEnd,
    onTouchMove,
    onTouchStart,
    onMouseMove,
    // defaults.
    tabIndex = 0,
    type = 'button',
    // Spreads anything else.
    ...rest
  } = props;
  const buttonRef = useRef(null);
  const [isFocused, setIsFocus] = useState(false);
  if (disabled && isFocused) setIsFocus(false); // Focus events

  function handleBlur(event) {
    setIsFocus(false);
    focusContext.current = null;
    if (onBlur) onBlur(event);
  }

  function handleFocus(event) {
    setIsFocus(true);
    focusContext.current = buttonRef.current;
    if (onFocus) onFocus(event);
  } // Mouse events


  function handleClick(event) {
    if (onClick) onClick(event);
  }

  function handleMouseDown(event) {
    if (onMouseDown) onMouseDown(event);
  }

  function handleMouseUp(event) {
    if (onMouseUp) onMouseUp(event);
  }

  function handleMouseEnter(event) {
    if (onMouseEnter) onMouseEnter(event);
  }

  function handleMouseLeave(event) {
    if (onMouseLeave) onMouseLeave(event);
  }

  function handleMouseMove(event) {
    if (onMouseMove) onMouseMove(event);
  } // Keyboard events


  function handleKeyDown(event) {
    if (isFocused && event.key === 'Escape' && focusContext.current) {
      focusContext.current.blur();
    } else if (onKeyDown) {
      onKeyDown(event);
    }
  }

  function handleKeyUp(event) {
    if (onKeyUp) onKeyUp(event);
  } // Touch events


  function handleTouchStart(event) {
    if (onTouchStart) onTouchStart(event);
  }

  function handleTouchEnd(event) {
    if (onTouchEnd) onTouchEnd(event);
  }

  function handleTouchMove(event) {
    if (onTouchMove) onTouchMove(event);
  }

  return /*#__PURE__*/jsx(elements$c.content, {
    ref: buttonRef,
    className: className,
    disabled: disabled,
    tabIndex: disabled ? -1 : tabIndex,
    onBlur: handleBlur,
    onClick: handleClick,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onMouseUp: handleMouseUp,
    onTouchEnd: handleTouchEnd,
    onTouchMove: handleTouchMove,
    onTouchStart: handleTouchStart,
    type: type,
    ...rest,
    children: children
  });
};

const elements$b = {
  baseInput: styled.input.withConfig({
    displayName: "base_inputcomponent__baseInput",
    componentId: "sc-1ga12ih-0"
  })(["display:block;font:inherit;color:currentColor;border:0;margin:0;padding:6px 0 7px;display:block;min-width:0;box-sizing:content-box;background:none;-webkit-tap-highlight-color:transparent;outline:none;font-size:1rem;"])
};
/**
 * # cortex:react-ui-std:comp:BaseInput
 */

const BaseInput = /*#__PURE__*/forwardRef(function BaseInput(props, ref) {
  const {
    // Takes out these values to be used as variables here.
    className,
    disabled,
    type,
    // defaults.
    tabIndex = 0,
    // Spreads anything else.
    ...rest
  } = props;
  const [isFocused, setIsFocus] = useState(false);
  if (disabled && isFocused) setIsFocus(false);
  return /*#__PURE__*/jsx(elements$b.baseInput, {
    ref: ref,
    "aria-invalid": true,
    "aria-describedby": '',
    className: className,
    disabled: disabled,
    tabIndex: disabled ? -1 : tabIndex,
    type: type,
    ...rest
  });
});

/**
 * ## cortex:react-ui-std:dangerouslyInjectHTML
 * ### 🚨  If you don't know exactly what you're doing, don't ever use this. 🚨
 */
function dangerouslyInjectHTML(htmlString) {
  // Reason: We've intentionally not included Node types. But how we're
  // distributing the bundle, this is fine. (Could add a x.d.ts that types
  // process though)
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (process.env.NODE_ENV === 'development') {
    console.warn(`
Unsafe HTML was injected into the DOM, if you know what you're doing you can ignore this message.
If not, use the "sanitizedInjectHTML" function instead.
		`);
  }

  return {
    __html: htmlString
  };
}

const elements$a = {
  svg: styled.svg.withConfig({
    displayName: "base_iconcomponent__svg",
    componentId: "sc-cyh2c-0"
  })(["width:1em;height:1em;display:inline-block;vertical-align:text-top;pointer-events:none;fill:currentColor;stroke:currentColor;flex-shrink:0;", ""], ({
    rotation,
    mirror,
    center
  }) => {
    // Needs to concat "transform" so they won't interfere with each other
    let transformBuilder = '';

    if (rotation !== 0) {
      transformBuilder += `rotate(${rotation}deg) `;
    }

    if (mirror !== null) {
      const horizontal = mirror === 'Horizontal' || mirror === true || false;
      const vertical = mirror === 'Vertical' || mirror === true || false;

      if (horizontal) {
        transformBuilder += 'scaleX(-1) ';
      }

      if (vertical) {
        transformBuilder += 'scaleY(-1) ';
      }
    }

    if (center) {
      transformBuilder += 'translateY(50%)';
      return `
					position: relative;
					top: 50%;
					transform: ${transformBuilder.trim()};
					transform-origin: 50% 100%;
					vertical-align: super;
				`;
    } else if (transformBuilder !== '') {
      return `transform: ${transformBuilder.trim()};`;
    }

    return null;
  })
};
/**
 * # cortex:react-ui-std:comp:BaseIcon
 */

const BaseIcon = ({
  children,
  viewBox,
  rotation,
  mirror,
  center,
  dangerouslySetInnerHTML
}) => {
  return /*#__PURE__*/jsx(elements$a.svg, {
    "data-testid": "svg",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: viewBox !== null && viewBox !== void 0 ? viewBox : '0 0 512 512',
    rotation: rotation !== null && rotation !== void 0 ? rotation : 0,
    mirror: mirror !== null && mirror !== void 0 ? mirror : null,
    center: center !== null && center !== void 0 ? center : false,
    dangerouslySetInnerHTML: dangerouslySetInnerHTML,
    children: children
  });
};

/**
 * # cortex:react-ui-std:helper:AnchorPointConsumer
 */

let AnchorPointConsumer;

(function (_AnchorPointConsumer) {
  let Events;

  (function (Events) {
    Events[Events["Update"] = 0] = "Update";
    Events[Events["Notify"] = 1] = "Notify";
  })(Events || (Events = {}));

  _AnchorPointConsumer.Events = Events;
  const defaultPosition = 'x-';
  const list = _AnchorPointConsumer.list = new Map();
  const notifier = _AnchorPointConsumer.notifier = new Emitter();

  class Instance {
    constructor({
      name,
      ref,
      left,
      top
    }) {
      this.vec2 = void 0;
      this.name = void 0;
      this.elementReference = void 0;
      this.top = void 0;
      this.left = void 0;
      this.eventEmitter = new Emitter();
      this.name = name;
      this.elementReference = ref;
      this.top = top !== null && top !== void 0 ? top : defaultPosition;
      this.left = left !== null && left !== void 0 ? left : defaultPosition;
      window.addEventListener('resize', this.handleWindowEvent.bind(this));
      window.addEventListener('scroll', this.handleWindowEvent.bind(this));
      this.vec2 = this.calculatePosition(this.top, this.left);
    }

    onChange(fn) {
      this.eventEmitter.on(Events.Update, fn);
      return () => {
        this.eventEmitter.off(Events.Update, fn);
      };
    }

    destroy() {
      window.removeEventListener('resize', this.handleWindowEvent.bind(this));
      window.removeEventListener('scroll', this.handleWindowEvent.bind(this));
    }

    handleWindowEvent() {
      this.vec2 = this.calculatePosition(this.top, this.left);
      this.eventEmitter.emit(Events.Update, this.vec2);
    }

    calculatePosition(top, left) {
      const rect = this.elementReference.getBoundingClientRect();

      function returnPercentageOfAnchorPointStr(str) {
        const length = str.length - 1;
        const index = str.indexOf('x');
        if (index === -1) throw new Error('no x');
        return index / length;
      }

      const topPercentage = returnPercentageOfAnchorPointStr(top);
      const leftPercentage = returnPercentageOfAnchorPointStr(left);
      const topPosition = rect.y + window.scrollY + rect.height * topPercentage;
      const leftPosition = rect.x + rect.width * leftPercentage;
      return new Vec2(leftPosition, topPosition);
    }

  }

  function add(configuration) {
    if (list.has(configuration.name)) {
      throw new Error(`Tried to add ${configuration.name} twice`);
    }

    list.set(configuration.name, new Instance(configuration));
    notifier.emit(Events.Notify, configuration.name);
  }

  _AnchorPointConsumer.add = add;

  function destroy(name) {
    if (!list.has(name)) {
      throw new Error(`Could not find an event with the name ${name}`);
    }

    const anchorPointInstance = list.get(name);
    anchorPointInstance === null || anchorPointInstance === void 0 ? void 0 : anchorPointInstance.destroy();
    list.delete(name);
  }

  _AnchorPointConsumer.destroy = destroy;
})(AnchorPointConsumer || (AnchorPointConsumer = {}));

const elements$9 = {
  container: styled.div.withConfig({
    displayName: "anchor_pointcomponent__container",
    componentId: "sc-19k057k-0"
  })(["display:contents;"])
};
/**
 * # cortex:react-ui-std:comp:AnchorPoint
 */

const AnchorPoint = ({
  name,
  top,
  left,
  children
}) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current === null) {
      throw new Error('Could not read DOM value');
    }

    if (ref.current.parentNode === null) {
      throw new Error('Ref has no parent to attach to');
    }

    AnchorPointConsumer.add({
      name,
      ref: ref.current.parentNode,
      top,
      left
    });
    return () => {
      AnchorPointConsumer.destroy(name);
    };
  }, []);
  return /*#__PURE__*/jsx(elements$9.container, {
    ref: ref,
    children: children
  });
};

const elements$8 = {
  article: styled.div.withConfig({
    displayName: "articlecomponent__article",
    componentId: "sc-ejwa7k-0"
  })(["display:contents;", " p{margin-bottom:10px;}h1{margin-bottom:30px;}h2{margin-bottom:30px;}h3{margin-bottom:30px;}h4{margin-bottom:30px;}h5{margin-bottom:15px;}h6{margin-bottom:5px;}"], ({
    center
  }) => center && `text-align: center;`)
};
/**
 * # cortex:react-ui:Article
 */

const Article = ({
  children,
  className,
  center,
  ...rest
}) => {
  return /*#__PURE__*/jsx(elements$8.article, {
    center: center,
    className: className,
    ...rest,
    children: children
  });
};

const elements$7 = {
  fluid: styled.span.withConfig({
    displayName: "sizecomponent__fluid",
    componentId: "sc-ua2hd6-0"
  })(["position:relative;display:inline-block;width:100%;& > *{width:100%;}"])
};
/**
 * # cortex:react-ui:Size
 */

const Size = ({
  children,
  className,
  fluid = false
}) => {
  if (fluid) {
    return /*#__PURE__*/jsx(elements$7.fluid, {
      className: className,
      children: children
    });
  }

  return /*#__PURE__*/jsx("span", {
    className: className,
    children: children
  });
};

const elements$6 = {
  button: styled(BaseButton).withConfig({
    displayName: "buttoncomponent__button",
    componentId: "sc-1snrpmp-0"
  })(["height:40px;padding:0px 16px;border-radius:20px;box-sizing:border-box;color:#ffffff;background-color:#0099ff;"])
};
/**
 * # cortex:react-ui:Button
 */

const Button = ({
  children,
  fluid,
  className,
  ...rest
}) => {
  return /*#__PURE__*/jsx(Size, {
    fluid: fluid,
    className: className !== null && className !== void 0 ? className : '',
    children: /*#__PURE__*/jsx(elements$6.button, { ...rest,
      children: children
    })
  });
};

const elements$5 = {
  flex: styled.div.withConfig({
    displayName: "flexcomponent__flex",
    componentId: "sc-1mxh8j5-0"
  })(["display:flex;", " ", " ", " ", " ", " ", " ", " ", ""], ({
    applySize
  }) => applySize && `width: 100%;\nheight: 100%;`, ({
    styleAlignItems
  }) => styleAlignItems && `align-items: ${styleAlignItems};`, ({
    styleJustifyContent
  }) => styleJustifyContent && `justify-content: ${styleJustifyContent};`, ({
    styleAlignSelf
  }) => styleAlignSelf && `align-selfs: ${styleAlignSelf};`, ({
    styleWrap
  }) => styleWrap && `flex-wrap: wrap;`, ({
    styleGrow
  }) => styleGrow && `flex-grow: ${styleGrow};`, ({
    styleShrink
  }) => styleShrink && `flex-shrink: ${styleShrink};`, ({
    styleBasis
  }) => styleBasis && `flex-basis: ${styleBasis};`)
};
/**
 * # cortex:react-ui:Flex
 * A layout component that's used to specify a layout of children using the Flexbox
 * algorithm.
 */

const Flex = ({
  grow,
  basis,
  className,
  children,
  shrink = 0,
  wrap = false,
  applySize = true,
  direction = 'column',
  alignItems = 'flex-start',
  justifyContent = 'flex-start'
}) => {
  return /*#__PURE__*/jsx(elements$5.flex, {
    className: className,
    styleDirection: direction,
    styleAlignItems: alignItems,
    styleJustifyContent: justifyContent,
    styleWrap: wrap,
    styleGrow: grow,
    styleShrink: shrink,
    styleBasis: basis,
    applySize: applySize,
    children: children
  });
};

/**
 * # cortex:react-ui-std:helper:StyleSheetController
 */
class StyleSheetController {
  constructor() {
    this.styleElement = void 0;
    this.styles = new Set();
    this.styleElement = document.createElement('style');
    this.styleElement.type = 'text/css';
    this.styleElement.appendChild(document.createTextNode(''));
    document.head.appendChild(this.styleElement);
  }

  addToRegister(selector, style) {
    const cssStyleSheet = this.styleElement.sheet;
    if (this.styles.has(selector)) return this;
    this.styles.add(selector);
    const newCSSVariables = `
			${selector} {
        ${style}
			}
    `;
    cssStyleSheet.insertRule(newCSSVariables, cssStyleSheet.cssRules.length);
    return this;
  }

}

const styleSheetController = new StyleSheetController();
/**
 * # cortex:react-ui-std:comp:HeadlessStack
 */

const HeadLessStack = ({
  axis,
  fluid,
  spacing,
  children
}) => {
  const halfSpacing = spacing / 2;
  const baseStr = `${spacing}_${fluid ? 'y' : 'n'}_${axis === 'Vertical' ? 'v' : 'h'}`;
  const parentClassName = `p_${baseStr}`;
  const childClassName = `c_${baseStr}`;

  if (axis === 'Vertical') {
    styleSheetController.addToRegister(`.${parentClassName}`, `
					display: flex;
					${fluid ? `height: calc(100% + ${spacing}px);` : ''}
					flex-direction: column;
					margin-top: -${halfSpacing}px!important;
					margin-bottom: -${halfSpacing}px!important;
				`).addToRegister(`.${childClassName}`, `
					${fluid ? `height: 100%;` : ''}
					margin-top: ${halfSpacing}px!important;
					margin-bottom: ${halfSpacing}px!important;
				`);
  } else {
    styleSheetController.addToRegister(`.${parentClassName}`, `
				display: flex;
				${fluid ? `width: calc(100% + ${spacing}px);` : ''}
				flex-direction: row;
				margin-left: -${halfSpacing}px!important;
				margin-right: -${halfSpacing}px!important;
			`).addToRegister(`.${childClassName}`, `
				${fluid ? `width: 100%;` : ''}
				margin-left: ${halfSpacing}px!important;
				margin-right: ${halfSpacing}px!important;
			`);
  }

  return children({
    parentClassName: parentClassName,
    childClassName: childClassName
  });
};

/**
 * # cortex:react-ui-std:helper:unwrapFragment
 */

function unwrapFragment(children) {
  const childArr = React.Children.toArray(children);
  const newArr = childArr.reduce((builder, child) => {
    if (child.type === Fragment) {
      return builder.concat(unwrapFragment(child.props.children));
    }

    builder.push(child);
    return builder;
  }, []);
  return newArr;
}

/**
 * # cortex:react-ui:HStack
 */
const HStack = ({
  className,
  spacing = 0,
  fluid = true,
  children
}) => {
  const flatten = unwrapFragment(children);
  return /*#__PURE__*/jsx(HeadLessStack, {
    spacing: spacing,
    axis: 'Horizontal',
    fluid: fluid,
    children: ({
      childClassName,
      parentClassName
    }) => /*#__PURE__*/jsx("div", {
      className: `${className !== null && className !== void 0 ? className : ''} ${parentClassName}`.trim(),
      children: flatten.map(child => {
        var _child$props$classNam;

        if (child === null) return child;
        return /*#__PURE__*/React.cloneElement(child, { ...child.props,
          className: `${(_child$props$classNam = child.props.className) !== null && _child$props$classNam !== void 0 ? _child$props$classNam : ''} ${childClassName}`.trim()
        });
      })
    })
  });
};

const elements$4 = {
  label: styled.label.withConfig({
    displayName: "inputcomponent__label",
    componentId: "sc-7av3og-0"
  })(["min-width:40px;display:block;position:relative;box-sizing:border-box;"]),
  input: styled(BaseInput).withConfig({
    displayName: "inputcomponent__input",
    componentId: "sc-7av3og-1"
  })(["height:40px;padding:0px 16px;box-sizing:border-box;border-radius:10px;color:var(--visiba_input_clr,rgb(51,51,51));background-color:var(--visiba_input_bg,rgb(51,51,51));width:100%;&:focus{outline-width:0px;box-shadow:inset 0 0 0 1px var(--visiba_input_focus_clr,#0099ff);}"])
};
/**
 * # cortex:react-ui:Input
 */

const Input = /*#__PURE__*/forwardRef(function Input({
  fluid,
  className,
  ...rest
}, ref) {
  return /*#__PURE__*/jsx(Size, {
    fluid: fluid,
    className: className,
    children: /*#__PURE__*/jsx(elements$4.label, {
      children: /*#__PURE__*/jsx(elements$4.input, { ...rest,
        ref: ref
      })
    })
  });
});

const elements$3 = {
  body: styled.div.withConfig({
    displayName: "layoutcomponent__body",
    componentId: "sc-1tkq8p3-0"
  })(["display:flex;box-sizing:", ";flex:", ";flex-shrink:", ";padding:", ";margin:", ";background:", ";border-radius:", ";box-shadow:", ";", " ", " ", " ", " ", ""], ({
    styleBox
  }) => styleBox === 'inner' ? 'border-box' : 'content-box', ({
    styleGrow
  }) => styleGrow ? '1' : null, ({
    styleShrink
  }) => styleShrink ? '1' : '0', ({
    stylePadding
  }) => stylePadding !== undefined ? typeof stylePadding === 'string' ? stylePadding : `${stylePadding}px` : '0', ({
    styleMargin
  }) => styleMargin !== undefined ? typeof styleMargin === 'string' ? styleMargin : `${styleMargin}px` : '0', ({
    styleBackground
  }) => styleBackground !== null && styleBackground !== void 0 ? styleBackground : null, ({
    styleCornerRadius
  }) => styleCornerRadius ? typeof styleCornerRadius === 'string' ? styleCornerRadius : `${styleCornerRadius}px` : null, ({
    styleShadow
  }) => styleShadow !== null && styleShadow !== void 0 ? styleShadow : null, ({
    styleWidth
  }) => {
    if (typeof styleWidth === 'number') {
      return `width: ${styleWidth}px;`;
    }

    if (Array.isArray(styleWidth)) {
      const [max, min] = styleWidth;
      let builder = 'width: 100%;';

      if (max !== null) {
        builder += `\nmax-width: ${max}px;`;
      } else {
        builder = '';
      }

      if (min !== undefined) {
        builder += `\nmin-width: ${min}px;`;
      }

      return builder;
    }

    if (typeof styleWidth === 'string') {
      return `width: ${styleWidth};`;
    }

    return 'width: 100%;';
  }, ({
    styleHeight
  }) => {
    if (typeof styleHeight === 'number') {
      return `height: ${styleHeight}px;`;
    }

    if (Array.isArray(styleHeight)) {
      const [max, min] = styleHeight;
      let builder = 'height: 100%;';

      if (max !== null) {
        builder += `\nmax-height: ${max}px;`;
      } else {
        builder = '';
      }

      if (min !== undefined) {
        builder += `\nmin-height: ${min}px;`;
      }

      return builder;
    }

    if (typeof styleHeight === 'string') {
      return `height: ${styleHeight};`;
    }

    return 'height: 100%;';
  }, ({
    styleDirection
  }) => {
    if (styleDirection !== undefined) {
      return `flex-direction: ${styleDirection};`;
    }

    return null;
  }, ({
    styleClip
  }) => {
    if (styleClip === undefined) {
      return null;
    }

    if (styleClip === 'x') {
      return 'overflow-x: auto\noverflow-y: hidden;';
    }

    if (styleClip === 'y') {
      return 'overflow-y: auto;\noverflow-x: hidden;';
    }

    if (styleClip === 'xy') {
      return 'overflow: auto;';
    }

    return 'overflow: hidden;';
  }, ({
    styleAlignment,
    styleDirection
  }) => {
    if (styleDirection === 'row') {
      switch (styleAlignment) {
        case 'center':
          return `
							justify-content: center;
							align-items: center;
						`;

        case 'leading':
          return `
							justify-content: flex-start;
							align-items: center;
						`;

        case 'trailing':
          return `
							justify-content: flex-end;
							align-items: center;
						`;

        case 'top':
          return `
							justify-content: center;
							align-items: flex-start;
						`;

        case 'bottom':
          return `
							justify-content: center;
							align-items: flex-end;
						`;

        case 'topLeading':
          return `
							justify-content: flex-start;
							align-items: flex-start;
						`;

        case 'topTrailing':
          return `
							justify-content: flex-end;
							align-items: flex-start;
						`;

        case 'bottomLeading':
          return `
							justify-content: flex-start;
							align-items: flex-end;
						`;

        case 'bottomTrailing':
          return `
							justify-content: flex-end;
							align-items: flex-end;
						`;

        case 'spaceCenter':
          return `
							justify-content: space-between;
							align-items: center;
						`;

        case 'spaceEnd':
          return `
							justify-content: space-between;
							align-items: flex-end;
						`;

        case 'spaceStart':
          return `
							justify-content: space-between;
							align-items: flex-start;
						`;

        case 'stretch':
          return `
							justify-content: stretch;
							align-items: stretch;
						`;

        default:
          throw new Error('unsupported alignment');
      }
    } else {
      switch (styleAlignment) {
        case 'center':
          return `
							justify-content: center;
							align-items: center;
						`;

        case 'leading':
          return `
							justify-content: center;
							align-items: flex-start;
						`;

        case 'trailing':
          return `
							justify-content: center;
							align-items: flex-end;
						`;

        case 'top':
          return `
							justify-content: flex-start;
							align-items: center;
						`;

        case 'bottom':
          return `
							justify-content: flex-end;
							align-items: center;
						`;

        case 'topLeading':
          return `
							justify-content: flex-start;
							align-items: flex-start;
						`;

        case 'topTrailing':
          return `
							justify-content: flex-start;
							align-items: flex-end;
						`;

        case 'bottomLeading':
          return `
							justify-content: flex-end;
							align-items: flex-start;
						`;

        case 'bottomTrailing':
          return `
							justify-content: flex-end;
							align-items: flex-end;
						`;

        case 'spaceCenter':
          return `
							justify-content: space-between;
							align-items: center;
						`;

        case 'spaceEnd':
          return `
							justify-content: space-between;
							align-items: flex-end;
						`;

        case 'spaceStart':
          return `
							justify-content: space-between;
							align-items: flex-start;
						`;

        case 'stretch':
          return `
							justify-content: stretch;
							align-items: stretch;
						`;

        default:
          throw new Error('unsupported alignment');
      }
    }
  })
};
/**
 * # cortex:react-ui:Layout
 * A layout component that's used to create a basic container with common
 * occurring constraints.
 */

const Layout = ({
  alignment = 'topLeading',
  direction = 'column',
  shrink = true,
  box = 'inner',
  background,
  cornerRadius,
  grow,
  height,
  padding,
  shadow,
  width,
  margin,
  clip,
  className,
  children,
  ...rest
}) => {
  return /*#__PURE__*/jsx(elements$3.body, {
    styleAlignment: alignment,
    styleDirection: direction,
    styleShrink: shrink,
    styleBox: box,
    styleBackground: background,
    styleCornerRadius: cornerRadius,
    styleGrow: grow,
    styleHeight: height,
    stylePadding: padding,
    styleShadow: shadow,
    styleWidth: width,
    styleMargin: margin,
    styleClip: clip,
    className: className,
    ...rest,
    children: children
  });
};

// const elements = {}

/**
 * # cortex:react-ui:LoaderDots
 */
const LoaderDots = ({
  backgroundColor,
  container
}) => {
  const loadingContainer = {
    width: (container === null || container === void 0 ? void 0 : container.width) || '2rem',
    height: (container === null || container === void 0 ? void 0 : container.height) || '2rem',
    display: (container === null || container === void 0 ? void 0 : container.display) || 'flex',
    justifyContent: (container === null || container === void 0 ? void 0 : container.justifyContent) || 'space-around',
    alignItems: (container === null || container === void 0 ? void 0 : container.alignItems) || 'center'
  };
  const loadingCircle = {
    display: 'block',
    width: '0.5rem',
    height: '0.5rem',
    backgroundColor: backgroundColor || '#555555',
    borderRadius: '0.25rem'
  };
  const loadingContainerVariants = {
    start: {
      transition: {
        staggerChildren: 0.2
      }
    },
    end: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const loadingCircleVariants = {
    start: {
      y: '0%'
    },
    end: {
      y: '100%'
    }
  };
  const loadingCircleTransition = {
    duration: 0.5,
    yoyo: Infinity,
    ease: 'easeInOut'
  };
  return /*#__PURE__*/jsxs(motion.div, {
    style: loadingContainer,
    variants: loadingContainerVariants,
    initial: "start",
    animate: "end",
    children: [/*#__PURE__*/jsx(motion.span, {
      style: loadingCircle,
      variants: loadingCircleVariants,
      transition: loadingCircleTransition
    }), /*#__PURE__*/jsx(motion.span, {
      style: loadingCircle,
      variants: loadingCircleVariants,
      transition: loadingCircleTransition
    }), /*#__PURE__*/jsx(motion.span, {
      style: loadingCircle,
      variants: loadingCircleVariants,
      transition: loadingCircleTransition
    })]
  });
};

const id = 'visiba-portal';
const className = `${id}-container`;

if (!document.getElementById(id)) {
  var _document$body;

  const root_element = document.createElement('div');
  root_element.id = id;
  (_document$body = document.body) === null || _document$body === void 0 ? void 0 : _document$body.appendChild(root_element);
} else {
  Logger.assert(Logger.Level.WARN, 'multiple instances of visiba-ui-std has been made');
}

const styleSheet = new StyleSheetController();
styleSheet.addToRegister('#visiba-portal', `
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		pointer-events: none;
	`);
styleSheet.addToRegister(className, `
		display: contents;
	`);

/**
 * # cortex:react-ui-std:comp:Portal
 */
const Portal = ({
  children
}) => {
  const mount = document.getElementById(id);
  const el = document.createElement('div');
  el.className = className;
  if (!mount) throw new Error("root element was removed, don't do that");
  useEffect(() => {
    mount.appendChild(el);
    return () => {
      mount.removeChild(el);
    };
  }, [mount, el]);
  return /*#__PURE__*/createPortal(children, el);
};

/**
 * # cortex:react-ui:ResetCss
 */

const ResetCss = createGlobalStyle(["html,body,div,span,applet,object,iframe,h1,h2,h3,h4,h5,h6,p,blockquote,pre,a,abbr,acronym,address,big,cite,code,del,dfn,em,img,ins,kbd,q,s,samp,small,strike,strong,sub,sup,tt,var,b,u,i,center,dl,dt,dd,menu,ol,ul,li,fieldset,form,label,legend,table,caption,tbody,tfoot,thead,tr,th,td,article,aside,canvas,details,embed,figure,figcaption,footer,header,hgroup,main,menu,nav,output,ruby,section,summary,time,mark,audio,video{margin:0;padding:0;border:0;font-size:100%;font:inherit;vertical-align:baseline;}article,aside,details,figcaption,figure,footer,header,hgroup,main,menu,nav,section{display:block;}menu,ol,ul{list-style:none;}*,*::before,*::after{box-sizing:inherit;}body{line-height:1;}html{box-sizing:border-box;-moz-osx-font-smoothing:grayscale;-webkit-font-smoothing:antialiased;-webkit-text-size-adjust:100%;text-rendering:auto;letter-spacing:normal;word-spacing:normal;text-transform:none;text-indent:0;text-shadow:none;}img{display:inline-block;height:auto;max-width:100%;vertical-align:middle;}a{font-family:inherit;font-size:inherit;font-weight:inherit;font-style:inherit;color:inherit;text-decoration:inherit;}[hidden]{display:none !important;}@media (prefers-reduced-motion:reduce){html:focus-within{scroll-behavior:auto;}*,*::before,*::after{animation-duration:0.01ms !important;animation-iteration-count:1 !important;transition-duration:0.01ms !important;scroll-behavior:auto !important;}}"]);

const ScreenReaderTextElement = styled.span.withConfig({
  displayName: "screen-reader-textcomponent__ScreenReaderTextElement",
  componentId: "sc-1vanyy4-0"
})(["border:0;clip:rect(1px,1px,1px,1px);clip-path:inset(50%);height:1px;margin:-1px;overflow:hidden;padding:0;position:absolute;width:1px;word-wrap:normal !important;"]);

/**
 * # cortex:react-ui:ScreenReaderText
 * Component to communicate content to screen readers without visually display it on the screen.
 */
const ScreenReaderText = ({
  children
}) => {
  return /*#__PURE__*/jsx(ScreenReaderTextElement, {
    children: children
  });
};

const elements$2 = {
  separator: styled.div.withConfig({
    displayName: "separatorcomponent__separator",
    componentId: "sc-1wlnrm9-0"
  })(["width:100%;display:flex;justify-content:center;"]),
  inner: styled.div.withConfig({
    displayName: "separatorcomponent__inner",
    componentId: "sc-1wlnrm9-1"
  })(["height:2px;width:calc(100% - ", "px);background:var(--kira_separator_bg,#343438);"], ({
    padding
  }) => padding)
};
/**
 * # cortex:react-ui:Separator
 */

const Separator = ({
  children,
  className,
  padding
}) => {
  return /*#__PURE__*/jsx(elements$2.separator, {
    "aria-hidden": "true",
    className: className,
    children: /*#__PURE__*/jsx(elements$2.inner, {
      padding: padding !== null && padding !== void 0 ? padding : 0,
      children: children
    })
  });
};

const elements$1 = {
  body: styled.span.withConfig({
    displayName: "spacercomponent__body",
    componentId: "sc-dusmos-0"
  })(["width:", ";height:", ";flex:0 0 auto;"], ({
    width
  }) => width, ({
    height
  }) => height)
};
/**
 * # cortex:react-ui:Spacer
 */

const Spacer = ({
  height = 0,
  width = 0
}) => {
  height = typeof height === 'number' && height > 0 ? `${height}px` : height;
  width = typeof width === 'number' && width > 0 ? `${width}px` : width;
  return /*#__PURE__*/jsx(elements$1.body, {
    height: height,
    width: width
  });
};

const elements = {
  button: styled.div.withConfig({
    displayName: "static_buttoncomponent__button",
    componentId: "sc-crdn8m-0"
  })(["height:40px;padding:0px 16px;border-radius:20px;box-sizing:border-box;color:#ffffff;background-color:#0099ff;"])
};
/**
 * # cortex:react-ui:StaticButton
 */

const StaticButton = ({
  children,
  fluid,
  className,
  ...rest
}) => {
  return /*#__PURE__*/jsx(Size, {
    fluid: fluid,
    className: className,
    children: /*#__PURE__*/jsx(elements.button, { ...rest,
      children: children
    })
  });
};

const TextElement = styled.span.withConfig({
  displayName: "textcomponent__TextElement",
  componentId: "sc-zzoqn8-0"
})(["", " ", " ", " ", " ", " ", " ", " ", " &:focus{outline:0;}"], ({
  font
}) => font && `font: ${font};`, ({
  color
}) => color && `color: ${color};`, ({
  fontFamily
}) => fontFamily && `font-family: ${fontFamily};`, ({
  fontSize
}) => fontSize && `font-size: ${fontSize};`, ({
  fontWeight
}) => fontWeight && `font-weight: ${fontWeight};`, ({
  lineHeight
}) => lineHeight && `line-height: ${lineHeight};`, ({
  preserveLineBreaks
}) => preserveLineBreaks && 'white-space: pre-line;', ({
  textTransform
}) => textTransform && `text-transform: ${textTransform};`);

/**
 * # cortex:react-ui:Text
 * Component to render text-elements.
 */
const Text = /*#__PURE__*/forwardRef(function Text({
  children,
  as: asElement = 'span',
  color = 'inherit',
  ...rest
}, ref) {
  return /*#__PURE__*/jsx(TextElement, {
    as: asElement,
    color: color,
    ref: ref,
    ...rest,
    children: children
  });
});
Text.h1 = /*#__PURE__*/forwardRef(function TextH1({
  children,
  ...rest
}, ref) {
  return /*#__PURE__*/jsx(Text, {
    as: "h1",
    ref: ref,
    ...rest,
    children: children
  });
});
Text.h2 = /*#__PURE__*/forwardRef(function TextH2({
  children,
  ...rest
}, ref) {
  return /*#__PURE__*/jsx(Text, {
    as: "h2",
    ref: ref,
    ...rest,
    children: children
  });
});
Text.h3 = /*#__PURE__*/forwardRef(function TextH3({
  children,
  ...rest
}, ref) {
  return /*#__PURE__*/jsx(Text, {
    as: "h3",
    ref: ref,
    ...rest,
    children: children
  });
});
Text.h4 = /*#__PURE__*/forwardRef(function TextH4({
  children,
  ...rest
}, ref) {
  return /*#__PURE__*/jsx(Text, {
    as: "h4",
    ref: ref,
    ...rest,
    children: children
  });
});
Text.h5 = /*#__PURE__*/forwardRef(function TextH5({
  children,
  ...rest
}, ref) {
  return /*#__PURE__*/jsx(Text, {
    as: "h5",
    ref: ref,
    ...rest,
    children: children
  });
});
Text.h6 = /*#__PURE__*/forwardRef(function TextH6({
  children,
  ...rest
}, ref) {
  return /*#__PURE__*/jsx(Text, {
    as: "h6",
    ref: ref,
    ...rest,
    children: children
  });
});
Text.p = /*#__PURE__*/forwardRef(function TextP({
  children,
  preserveLineBreaks = true,
  ...rest
}, ref) {
  return /*#__PURE__*/jsx(Text, {
    as: "p",
    preserveLineBreaks: preserveLineBreaks,
    ref: ref,
    ...rest,
    children: children
  });
});

/**
 * # cortex:react-ui:VStack
 */
const VStack = function VStack({
  className,
  spacing = 0,
  fluid = true,
  children
}) {
  const flatten = unwrapFragment(children);
  return /*#__PURE__*/jsx(HeadLessStack, {
    spacing: spacing,
    axis: 'Vertical',
    fluid: fluid,
    children: ({
      childClassName,
      parentClassName
    }) => /*#__PURE__*/jsx("div", {
      className: `${className !== null && className !== void 0 ? className : ''} ${parentClassName}`.trim(),
      children: flatten.map(child => {
        var _child$props$classNam;

        if (child === null) return child;
        return /*#__PURE__*/React.cloneElement(child, { ...child.props,
          className: `${(_child$props$classNam = child.props.className) !== null && _child$props$classNam !== void 0 ? _child$props$classNam : ''} ${childClassName}`.trim()
        });
      })
    })
  });
};

/**
 * # cortex:react-ui-std:hooks:useAnchorPointValue
 */
function useAnchorPointValue(name) {
  const [position, setPosition] = useState(null);
  useEffect(() => {
    let disposer;

    if (AnchorPointConsumer.list.get(name)) {
      const anchorPointInstance = AnchorPointConsumer.list.get(name);
      setPosition(anchorPointInstance.vec2.getAsObj());
      disposer = anchorPointInstance.onChange(vec2 => {
        setPosition(vec2.getAsObj());
      });
    } else {
      function listener(event) {
        if (event !== name) return;
        AnchorPointConsumer.notifier.off(AnchorPointConsumer.Events.Notify, listener);
        const anchorPointInstance = AnchorPointConsumer.list.get(name);
        setPosition(anchorPointInstance.vec2.getAsObj());
        disposer = anchorPointInstance.onChange(vec2 => {
          setPosition(vec2.getAsObj());
        });
      }

      AnchorPointConsumer.notifier.on(AnchorPointConsumer.Events.Notify, listener);
    }

    return () => {
      if (disposer) disposer();
    };
  }, []);
  return position;
}

const Inner = function HeadlessPopover_Inner({
  attachTo,
  children
}) {
  const position = useAnchorPointValue(attachTo);
  return /*#__PURE__*/jsx(Fragment, {
    children: position !== null && children({
      x: position.x,
      y: position.y
    })
  });
};
/**
 * # cortex:react-ui-std:comp:HeadlessPopover
 */


const HeadLessPopover = ({
  attachTo,
  children
}) => {
  return /*#__PURE__*/jsx(Portal, {
    children: /*#__PURE__*/jsx(Inner, {
      attachTo: attachTo,
      children: children
    })
  });
};

/**
 * # cortex:react-ui-std:helper:isFocused
 */
const isFocused = event => {
  let originIsFocused = false;

  if (event.target && event.target instanceof Element && event.target !== document.body) {
    originIsFocused = document.activeElement === event.target;
  }

  return originIsFocused;
};

/**
 * ## cortex:react-ui-std:sanitizedInjectHTML
 * Sanitization is the inspection of an untrusted value, turning it into a value that's safe to insert into the DOM.
 */

function sanitizedInjectHTML(htmlString) {
  return {
    __html: DOMPurify.sanitize(htmlString)
  };
}

// Since the exported type below are so convoluted, we'll just disable these for
const formStates = new WeakMap();
function createFormGroup(controllers) {
  return {
    controllers,
    refs: {},

    isValid() {// being worked on in the new patient web
      // noop
    },

    state() {
      const builder = {};
      Object.entries(this.controllers).forEach(([key, value]) => {
        builder[key] = value.value;
      });
      return builder;
    },

    reset() {// being worked on in the new patient web
      // noop
    }

  };
}
function formControl(defaultValue, ...validators) {
  return {
    initialValue: defaultValue,
    value: defaultValue,
    valid: false,
    touched: false,
    validators: [...validators]
  };
}
/**
 * # cortex:react-ui-std:hooks:useForm
 */

function useForm(control) {
  const [formState] = useState(control);
  let formGroup;

  if (formStates.has(formState)) {
    formGroup = formStates.get(formState);
  } else {
    formGroup = createFormGroup(formState);
    formStates.set(formState, formGroup);
  }

  function handleInputChange(event) {
    if (!(event && event.srcElement)) return;
    const {
      value,
      name
    } = event.srcElement;
    formState[name].value = value;
  }
  /** Binds an input to this hook. */


  function set(ref) {
    if (ref) {
      if (typeof formState[ref.name] === 'undefined') {
        throw new Error(`"${ref.name}" is not a valid form group`);
      }

      ref.value = formState[ref.name].value;

      if (!formGroup.refs[ref.name]) {
        ref.addEventListener('input', handleInputChange);
        formGroup.refs[ref.name] = ref;
      }
    }
  }
  /** Wrapper for the a submit callback. */


  const forward = callback => event => {
    event.preventDefault();
    event.persist();
    callback(formGroup.state());
  };

  useEffect(() => {
    return () => {
      Object.values(formGroup.refs).forEach(ref => {
        ref.removeEventListener('input', handleInputChange);
      });
    };
  }, []);
  return {
    set,
    forward,
    formGroup
  };
}

/**
 * # cortex:react-ui-std:hooks:useKeyPress
 */

function useKeyPress(targetKey) {
  // State for keeping track of whether key is pressed
  const [keyPressed, setKeyPressed] = useState(false); // If pressed key is our target key then set to true

  function handleKeyDown({
    key
  }) {
    if (key === targetKey) {
      setKeyPressed(true);
    }
  } // If released key is our target key then set to false


  const handleKeyUp = ({
    key
  }) => {
    if (key === targetKey) {
      setKeyPressed(false);
    }
  }; // Add event listeners


  useEffect(() => {
    addEventListener('keydown', handleKeyDown);
    addEventListener('keyup', handleKeyUp); // Remove event listeners on cleanup

    return () => {
      removeEventListener('keydown', handleKeyDown);
      removeEventListener('keyup', handleKeyUp);
    };
  }, []); // Empty array ensures that effect is only run on mount and unmount

  return keyPressed;
}

/**
 * # cortex:react-ui-std:hooks:useOutsideClick
 */

function useOutsideClick(ref, handler) {
  const handleClick = event => {
    if (ref && (!ref.current || ref.current.contains(event.target))) {
      return;
    }

    handler(event);
  };

  useEffect(() => {
    addEventListener('mousedown', handleClick);
    addEventListener('touchstart', handleClick); // disposer

    return () => {
      removeEventListener('mousedown', handleClick);
      removeEventListener('touchstart', handleClick);
    };
  }, []);
}

export { AnchorPoint, AnchorPointConsumer, Article, BaseButton, BaseIcon, BaseInput, Button, Flex, HStack, HeadLessPopover, HeadLessStack, Input, Layout, LoaderDots, Portal, ResetCss, ScreenReaderText, Separator, Size, Spacer, StaticButton, Text, VStack, createFormGroup, dangerouslyInjectHTML, focusContext, formControl, isFocused, sanitizedInjectHTML, unwrapFragment, useAnchorPointValue, useForm, useKeyPress, useOutsideClick };
